import React, { useState, useMemo, useEffect, useRef } from 'react'
import debounce from 'debounce'
import get from 'lodash/get'
import { useFormContext } from '../../Form/FormContext'
import warning from 'tiny-warning'
import { FormValidation } from '../../Form/Form.types'
import RelayError from '../../../errors/errors'
import { isPromise } from '../../../helpers/helpers'
import useDatasource from '../../useDatasource/useDatasource'
import { DataSourceType } from '../Input.types'
import Label from '../Label/Label'
import Icon from '../../Icon/Icon'
import useValidation, { InputValidation } from '../useValidation'
import { LabelProps } from '../Label/Label.types'
import { ButtonProps } from '../../Button/Button.types'
import Filter, { IFilter } from '../../../helpers/filter'
import { IconProps, IconType } from '../../Icon/Icon.types'
import { gql, useQuery } from '@apollo/client'
import merge from 'lodash/merge'

export interface UseInputOptions {
    name: string
    value?: any
    label?: string | LabelProps
    disconnectFromForm?: boolean
    dataSource?: any
    validation?: InputValidation
    onChange?: (newValue, data?, opt?) => any
    onFocus?: any
    onMount?: any
    onUnmount?: any
    onBlur?: any
    onValidityChange?: any
    textField?: string
    valueField?: string
    filter?: IFilter
    isDisabled?: boolean
    isDisplay?: boolean
    useInternalState?: boolean
    suppressAutoSelect?: boolean
    noLabel?: boolean
    isRequired?: boolean
    hideValidationContainer?: boolean
    hideValidationState?: boolean
    labelPos?: string
    labelStyle?: 'default' | 'merged'
    description?: string
    placeholder?: string
    searchQuery?: string
    runQuery?: boolean
    graphVariables?: any
    useGraphQlQuery?: boolean
    graphQuery?: (v) => any
    button?: ButtonProps
    uniqueOn?: string
    dataQueryKey?: string
    formatQuery?: (val) => any
    unhookFromForm?: boolean
    showValidationOnDirty?: boolean
    placeholderIcon?: IconType
    placeholderIconProps?: IconProps
    icon?: IconType
}

interface UseInputProps {}

const useInput = (
    {
        name,
        dataSource,
        value,
        validation,
        onChange,
        onFocus,
        onBlur,
        onMount,
        onUnmount,
        textField,
        valueField,
        filter,
        unhookFromForm = false,
        isDisabled = false,
        isDisplay = false,
        disconnectFromForm = false,
        useInternalState = false,
        suppressAutoSelect = false,
        label,
        noLabel,
        onValidityChange,
        isRequired,
        hideValidationContainer = false,
        hideValidationState = false,
        labelPos = 'top',
        labelStyle = 'default',
        searchQuery,
        graphVariables = {},
        useGraphQlQuery = false,
        graphQuery = () => {},
        runQuery = true,
        button,
        uniqueOn,
        dataQueryKey,
        formatQuery,
        showValidationOnDirty = false,
        description,
        placeholder,
        placeholderIcon,
        placeholderIconProps,
        icon,
    }: UseInputOptions,
    valueType?: string,
    defaultValue?: any,
) => {
    const { data, isFetching, isLoading } = useDatasource(dataSource, searchQuery, runQuery)
    if (!name) {
        throw new RelayError('Input is missing a name: Please add a prop name')
    }
    if (data && typeof data[0] === 'object' && !valueField) {
        throw new RelayError(`Input ${name}'s datasource requires a valueField prop`)
    }
    if (data && typeof data[0] === 'object' && !textField) {
        throw new RelayError(`Input ${name}'s datasource requires a textField prop`)
    }
    //Internal
    const cleanValue = useRef()
    const initialValue = defaultValue && !value ? defaultValue : value

    const [initialLoadComplete, setInitialLoadComplete] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [internalValue, setInternalValue] = useState(initialValue)
    const [shallValidate, setShallValidate] = useState(true)

    const form = disconnectFromForm || unhookFromForm ? undefined : useFormContext()

    let _value = useInternalState ? internalValue : initialValue
    const _validation = validation || {}
    const _isRequired = isRequired || (validation && validation.test === 'required')

    if (form && !unhookFromForm) {
        _value = get(form.values, name)
    }
    const constructLabel = () => {
        if (noLabel || typeof label === 'object') {
            return null
        }

        let _label = label
        if (!label) {
            _label = name
                .toString()
                .split(/(?=[A-Z])/)
                .join(' ')
                .replace(/\b\w/g, function (l) {
                    return l.toUpperCase()
                })
        }
        return _label
    }
    const constructedLabel = constructLabel()

    const { validate, message, isValid, isValidating } = useValidation({
        name,
        value: _value,
        label: constructedLabel,
        form,
        validation,
    })
    const isInvalid = !isValid && isDirty

    const getValueFromDataItem = (dataItem) => {
        if (typeof dataItem === 'object') {
            return get(dataItem, valueField)
        }
        return dataItem
    }

    const contructedGraphQuery = useMemo(() => {
        if (useGraphQlQuery && graphQuery) {
            return graphQuery(searchQuery)
        }
        return gql`
            query none {
                none
            }
        `
    }, [searchQuery])

    const graphData = useQuery(contructedGraphQuery, {
        variables: merge({}, graphVariables, { searchQuery }),
        skip: !useGraphQlQuery || !searchQuery || searchQuery.length < 1,
    })

    const getData = () => {
        let _data = data
        if (useGraphQlQuery) {
            _data = get(graphData.data, dataQueryKey)
        } else if (dataQueryKey) {
            _data = get(data, dataQueryKey)
        }
        _data = Array.isArray(_data) ? _data.slice() : []

        if (uniqueOn !== undefined) {
            _data = _data.reduce((acc, curr) => {
                const uniqueVal = curr[uniqueOn]

                if (!acc.find((item) => item[uniqueOn] === uniqueVal)) {
                    acc.push(curr)
                }

                return acc
            }, [])
        }

        if (filter) {
            const _filter = new Filter(filter, _data)
            _data = _filter.filter(_data)

            //    If value is not contained in dataSource remove it
        }

        return _data
    }

    const _data = getData()

    const context = {
        name,
        isValid,
        setShallValidate,
        value: _value,
    }

    //On Component mount
    useEffect(() => {
        setInitialLoadComplete(true)

        cleanValue.current = _value

        if (form) {
            form.registerInput(name, _value, validation, {
                setIsDirty,
            })
        }
        if (onMount) {
            onMount(context)
        }
        return () => {
            if (onUnmount) {
                onUnmount(context)
            }
        }
    }, [])

    // On Data change
    useEffect(() => {
        //Automatically select if the data item only has one options
        if (!suppressAutoSelect && _data && _data.length === 1) {
            const dataItem = _data[0]
            let dataItemValue = valueField ? get(dataItem, valueField) : dataItem

            if (valueType === 'array') {
                dataItemValue = [dataItemValue]
            }

            if (JSON.stringify(dataItemValue) !== JSON.stringify(_value)) {
                _onChange(dataItemValue)
            }
        }
    }, [_data])

    //Set Dirty
    useEffect(() => {
        const valueSameAsOriginal = JSON.stringify(_value) === JSON.stringify(cleanValue.current)
        if (!valueSameAsOriginal) {
            setIsDirty(true)
        }
    }, [_value])

    //Validate
    useEffect(() => {
        if (!isFocused && shallValidate) {
            validate(_value)
        }
    }, [_value, isDirty, isFocused])

    //On validaty Change
    useEffect(() => {
        if (onValidityChange) {
            onValidityChange({ name, isValid, isValidating })
        }
    }, [isValid, isValidating])

    //
    // const { isValid, isValidating } = useValidation({
    //     name: name,
    //     form: 1,
    //     value: 1,
    //     validation: _validation,
    // })
    //
    // warning(
    //     !(form !== undefined && value !== undefined),
    //     `useInput: Input "${name}"'s value is being controlled by a form context. Add a prop "disconnectFromForm" if you wish to control outside of the form`,
    // )
    //
    // warning(
    //     !(form !== undefined && onChange !== undefined),
    //     `useInput: Input "${name}"'s onChange is being controlled by a form context. Add a prop "disconnectFromForm" if you wish to control outside of the form`,
    // )

    const handleChange = (_value, opt = {}) => {
        if (useInternalState) {
            setInternalValue(_value)
        }
        if (onChange) {
            onChange(_value, _data, opt)
        }
        if (form) {
            form.setValue(name, _value)
        }
    }

    const _onChange = (value, opt = {}) => {
        if (!isDirty) {
            setIsDirty(true)
        }

        if (isDisabled) {
            return
        }
        handleChange(value, opt)
    }

    const _onBlur = () => {
        if (!initialLoadComplete) {
            return
        }

        if (onBlur) {
            onBlur(_value)
        }
        setIsDirty(true)
        setIsFocused(false)
    }

    const _onFocus = () => {
        if (!initialLoadComplete) {
            return
        }
        if (onFocus) {
            onFocus(_value)
        }
        setIsFocused(true)
    }

    const classNames = () => {
        const c = ['r-input-wrapper']

        if (isValidating) {
            c.push('r-state-validating')
        }

        if (isInvalid && !hideValidationState) {
            c.push('r-state-invalid')
        }

        if (isFocused) {
            c.push('r-state-focused')
        }

        if (typeof label === 'object' && label.pos === 'left') {
            c.push('r-label-left')
        }

        if (isDisabled) {
            c.push('r-state-disabled')
        }

        return c.join(' ')
    }

    return {
        name,
        validation: _validation,
        validationMessage: message,
        form,
        data: _data,
        label,
        noLabel,
        onChange: _onChange,
        onBlur: _onBlur,
        onFocus: _onFocus,
        value: _value,
        isDisabled,
        isDisplay,
        isDirty,
        isFocused,
        isLoadingData: isLoading || graphData.loading,
        isFetchingData: isFetching,
        isValid,
        isValidating,
        isRequired: _isRequired,
        initialLoadComplete,
        button,
        labelStyle,
        textField,
        valueField,
        getValueFromDataItem,
        showValidationOnDirty,
        description,
        placeholder,
        placeholderIcon,
        placeholderIconProps,
        icon,
        hideValidationState,
    }
}

export default useInput
